// Umami payload key 標準化：xlsx 規格 payload 全為 snake_case；
// 在 plugin 層統一轉換，所有 caller（$analytics.push 分派 / 直接 $umami.track）都自動合規。
const camelToSnake = (s) => s.replace(/[A-Z]/g, (m, i) => (i ? '_' : '') + m.toLowerCase())
const toSnakeKeys = (obj) => {
  if (!obj || typeof obj !== 'object') return obj
  const out = {}
  for (const k in obj) out[camelToSnake(k)] = obj[k]
  return out
}

// UTM 持久化（last-touch attribution）
// 動機：Umami 的 Sources/Channels 報表是 session 級歸因，依賴第一個 pageview 的 url query string。
// PWA 從桌面啟動 / FB-IG-LINE WebView / 直接輸入網址 的 session 因為沒有 document.referrer 且
// URL 無 UTM，全部歸到 "(無)"，導致 Revenue → Sources 看不到真實投放成效。
// 做法：URL 一旦帶 utm_* 就存進 localStorage（最近一次覆蓋舊值），之後每次 pageview 若 URL
// 沒帶 utm_* 就用 stored 值補回 url query，讓 Umami server 端能正確歸因每個新 session。
// 30 天 TTL 對齊主流廣告平台 click-through window（FB 7/28d、Google 30/90d）的中位數。
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id']
const UTM_TTL_MS = 30 * 24 * 60 * 60 * 1000
// 敏感 query key 白名單（pageview URL 補回時剝除）：避免 token / OTP / 手機號等
// 經由 Umami URL 與 stored UTM 一起被 server 收集
const SENSITIVE_QUERY_KEYS = new Set(['token', 'otp', 'phone', 'code', 'password', 'auth', 'access_token'])

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  const { umamiWebsiteId, umamiScriptUrl, umamiDomains, umamiDebug, clientId } = config.public || {}
  const debug = umamiDebug === '1' || umamiDebug === 1 || umamiDebug === true

  if (!umamiWebsiteId || !umamiScriptUrl) {
    return {
      provide: {
        umami: { track: () => {}, enabled: false },
      },
    }
  }

  // localStorage key 以 clientId 作 namespace，避免同瀏覽器跨品牌 PWA 互相覆蓋 UTM
  const UTM_STORAGE_KEY = `__umami_utm_${clientId || 'default'}`

  const readUtmStore = () => {
    try {
      const raw = localStorage.getItem(UTM_STORAGE_KEY)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      if (!parsed || !parsed.utm || !parsed.ts) return null
      if (Date.now() - parsed.ts > UTM_TTL_MS) {
        localStorage.removeItem(UTM_STORAGE_KEY)
        return null
      }
      return parsed.utm
    } catch (e) {
      // 隱私模式 / quota 滿 / storage 已停用情境下靜默失效，但保留 dev 觀察點
      if (debug) console.warn('[umami] readUtmStore failed:', e?.message || e)
      return null
    }
  }

  const captureUtm = (search) => {
    const params = new URLSearchParams(search || '')
    const utm = {}
    for (const key of UTM_KEYS) {
      const v = params.get(key)
      if (v) utm[key] = v
    }
    if (Object.keys(utm).length === 0) return
    try {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify({ utm, ts: Date.now() }))
    } catch (e) {
      if (debug) console.warn('[umami] captureUtm failed:', e?.message || e)
    }
  }

  const buildAttributedUrl = (path, search) => {
    const original = new URLSearchParams(search || '')
    // 先剝敏感 query：避免 token / OTP / phone 被送進 Umami URL
    for (const key of [...original.keys()]) {
      if (SENSITIVE_QUERY_KEYS.has(key.toLowerCase())) original.delete(key)
    }
    // 現在的 URL 自己有 utm_* 就尊重之，不覆蓋（last-touch 已在 captureUtm 時更新）
    if (UTM_KEYS.some((k) => original.has(k))) {
      const qs = original.toString()
      return qs ? `${path}?${qs}` : path
    }
    const stored = readUtmStore()
    if (stored) {
      for (const [k, v] of Object.entries(stored)) original.set(k, v)
    }
    const qs = original.toString()
    return qs ? `${path}?${qs}` : path
  }

  // 初次載入先攔截 landing URL 的 utm_*，確保即使第一個 pageview 在 router.afterEach 之前發生，
  // 後續 session（含 Umami 因 inactivity timeout 切出的新 session）也能拿到 UTM 歸因。
  captureUtm(window.location.search)

  if (!window.__umamiQueue) window.__umamiQueue = []

  // Umami v2：track(string, obj) 帶 name → custom event；track(fn) 回傳不含 name → pageview。
  // 用 kind 區分兩條路徑，pageview 一律走 fn 形式，避免被當自訂事件記到 Events tab。
  // track() 回傳 Promise，需 attach .catch 吸收 async reject（例如 server 回 "Event limit reached"、
  // 429、CORS 等），否則會冒泡為 unhandled promise rejection 污染 console / Sentry。
  const sendToUmami = (kind, event, payload) => {
    const ret = kind === 'pageview' ? window.umami.track((props) => ({ ...props, ...(payload || {}) })) : window.umami.track(event, payload)
    if (ret && typeof ret.then === 'function') {
      ret.catch((err) => {
        if (debug) console.warn('[umami] send rejected', err)
      })
    }
  }

  const flushQueue = () => {
    if (!window.umami || typeof window.umami.track !== 'function') return
    const queue = window.__umamiQueue.splice(0)
    queue.forEach(([kind, event, payload]) => {
      try {
        sendToUmami(kind, event, payload)
      } catch (e) {
        if (debug) console.warn('[umami] flush error', e)
      }
    })
  }

  let isLoaded = false
  const initUmami = () => {
    if (isLoaded) return
    isLoaded = true
    const script = document.createElement('script')
    script.defer = true
    script.src = umamiScriptUrl
    script.setAttribute('data-website-id', umamiWebsiteId)
    script.setAttribute('data-auto-track', 'false')
    // 不設 data-exclude-search：Umami 後端 parser 完全依賴 url 字串中的 query 來歸類 UTM，
    // 排除後 paid 流量會被誤判為 organic。手動 pageview payload 帶完整 query 之前，
    // buildAttributedUrl 會剝掉 SENSITIVE_QUERY_KEYS（token/OTP/phone 等）。
    script.setAttribute('data-exclude-hash', 'true')
    if (umamiDomains) script.setAttribute('data-domains', umamiDomains)
    script.onload = flushQueue
    document.head.appendChild(script)

    // removeEventListener options 只看 capture，passive 會被忽略；省略以避免 reviewer 誤解
    events.forEach((event) => window.removeEventListener(event, initUmami))
  }

  const events = ['scroll', 'mousemove', 'touchstart', 'keydown', 'click']
  events.forEach((event) => {
    window.addEventListener(event, initUmami, { passive: true, once: true })
  })

  setTimeout(initUmami, 5000)

  const dispatch = (kind, event, payload) => {
    const normalized = toSnakeKeys(payload)
    if (debug) console.info('[umami]', kind, event, normalized)
    if (window.umami && typeof window.umami.track === 'function') {
      try {
        sendToUmami(kind, event, normalized)
      } catch (e) {
        if (debug) console.warn('[umami] track error', e)
      }
    } else {
      window.__umamiQueue.push([kind, event, normalized])
    }
  }

  const track = (event, payload) => {
    if (!event) return
    dispatch('event', event, payload)
  }

  const trackPageview = (payload) => dispatch('pageview', null, payload)

  // Umami-only 頁面層事件：每次 route 變更時送 open_page + page_view，不經 $analytics（避免 modal push 污染）
  // payload 對齊 xlsx 規格：location = pathname、page_title = document.title
  const router = useRouter()
  if (router) {
    router.afterEach(() => {
      setTimeout(() => {
        const path = window.location.pathname
        const search = window.location.search
        const page_title = document.title
        // 每次導航都嘗試攔截新出現的 UTM（例如 deep link 開啟、廣告中途切換 campaign）
        captureUtm(search)
        track('open_page', { location: path, page_title, window_type: 'tab', action_source: 'navigation' })
        // pageview 走函式形式（不帶 name），讓 Umami 計入 Views/Sessions
        // url 帶 search 並補回 stored utm_*，Umami 後端 parser 才能從 query 抽出 UTM 歸類 paid 流量
        trackPageview({ url: buildAttributedUrl(path, search), title: page_title })
      }, 0)
    })
  }

  return {
    provide: {
      umami: { track, enabled: true },
    },
  }
})
